import React from "react"
import { FormattedMessage } from "react-intl"
import { Avatar, Box, Button, Stack, Typography } from "@carrotfertility/carotene-core"
import {
  useCarrotCompanion,
  useCarrotCompanionCarrotPlanMessage
} from "#/components/care-team/hooks/useCarrotCompanion"
import { useHistory } from "react-router-dom"
import { getParsedHtmlElements } from "#/components/messaging/utils"
import { getPiiProps } from "#/services/tracking"
import { useInAppMessaging } from "#/components/context/messaging/InAppMessagingContext"
import { updateCarrotPlanMessageDismissTimestamp } from "#/components/views/modal/utils/localStorageUtils"
import { formatCarrotCompanionName } from "./utils/carrotCompanionNameUtil"
import { CarrotCompanionTile } from "#/components/care-team/CarrotCompanionTile"
import { useMemberActionTracking } from "#/services/memberActionTracking/useMemberActionTracking"
import { MemberActions } from "#/services/memberActionTracking/memberActions"
import { BorderedInfoBox } from "#/components/shared/BorderedInfoBox"
import { useGetCarrotPlanRequestStatus } from "#/components/carrot-plans/hooks/useCarrotPlanRequestStatus"

export function CarrotCompanionCarrotPlanMessage({ onDismiss }: { onDismiss: () => void }): JSX.Element | null {
  const {
    data: carrotCompanion,
    isLoading: isCarrotCompanionLoading,
    isError: isCarrotCompanionError
  } = useCarrotCompanion()

  const cpRequestStatusQuery = useGetCarrotPlanRequestStatus()
  const carrotPlanIsOnTheWay = cpRequestStatusQuery.data?.carrotPlanIsOnTheWay

  const { data: companionPlanMessage, isLoading: isCarrotCompanionCarrotPlanMessageLoading } =
    useCarrotCompanionCarrotPlanMessage()
  const thread = companionPlanMessage?.thread
  const carrotPlanMessage = companionPlanMessage?.carrotPlanMessage

  const { setSelectedThread } = useInAppMessaging()
  const history = useHistory()
  const { mutate: trackAction } = useMemberActionTracking()

  const shouldHideMessage =
    isCarrotCompanionCarrotPlanMessageLoading ||
    isCarrotCompanionLoading ||
    isCarrotCompanionError ||
    !carrotCompanion ||
    !carrotPlanMessage?.htmlBody ||
    carrotPlanIsOnTheWay

  const { displayName, initials } = formatCarrotCompanionName(carrotCompanion?.name)

  const handleReply = () => {
    trackAction({
      memberActionTaken: MemberActions.HOMEPAGE_CARROT_COMPANION_REPLY_CLICKED
    })
    setSelectedThread(thread)
    updateCarrotPlanMessageDismissTimestamp()
    history.push("/messages")
  }

  const handleDismiss = () => {
    trackAction({
      memberActionTaken: MemberActions.HOMEPAGE_CARROT_COMPANION_DISMISS_CLICKED
    })
    onDismiss()
  }

  if (shouldHideMessage) {
    return <CarrotCompanionTile />
  }

  return (
    <BorderedInfoBox>
      <Stack gap={(theme) => theme.tokens.spacing.xl}>
        <Box
          {...getPiiProps()}
          flexDirection="column"
          sx={{
            whiteSpace: "pre-line",
            lineHeight: (theme) => theme.typography.body1.lineHeight,
            wordBreak: "break-word",
            ...(theme) => theme.typography.body1
          }}
        >
          {getParsedHtmlElements(carrotPlanMessage?.htmlBody)}
        </Box>
        <Stack gap={(theme) => theme.tokens.spacing.md} direction="column" justifyContent="space-between">
          <Stack gap={(theme) => theme.tokens.spacing.xs} direction="row">
            {carrotCompanion.photoUrl ? (
              <Avatar src={carrotCompanion.photoUrl} alt={carrotCompanion.name} />
            ) : (
              <Avatar sx={{ bgcolor: (theme) => theme.palette.warning.contrastText }}>{initials}</Avatar>
            )}

            <Stack>
              <Typography variant="h5">{displayName}</Typography>
              <Typography variant="body2">
                <FormattedMessage defaultMessage="Your Carrot Companion" />
              </Typography>
            </Stack>
          </Stack>

          <Stack alignItems="start" direction="row" gap={(theme) => theme.tokens.spacing.xs}>
            <Button
              onClick={handleReply}
              color="secondary"
              variant="contained"
              fullWidth={false}
              data-analytics-id="care-team-carrot-companion-plan-message-reply-button"
            >
              <FormattedMessage defaultMessage="Reply" />
            </Button>

            <Button
              onClick={handleDismiss}
              color="secondary"
              variant="outlined"
              fullWidth={false}
              data-analytics-id="care-team-carrot-companion-plan-message-dismiss-button"
            >
              <FormattedMessage defaultMessage="Dismiss" />
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </BorderedInfoBox>
  )
}
